import type { ColumnDef } from '@tanstack/react-table';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/atoms';
import { DataTable } from '@/components/molecules';
import { routes } from '@/lib/constants';
import type { BulkUploadResultRow, BulkUploadSubmitData } from '@/types';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { Link } from 'react-router';

interface BulkUploadResultStepProps {
  result: BulkUploadSubmitData;
  onStartOver: () => void;
}

const columns: ColumnDef<BulkUploadResultRow>[] = [
  {
    accessorKey: 'row',
    header: 'Row',
    cell: ({ row }) => <span className="font-medium">{row.original.row}</span>,
  },
  {
    accessorKey: 'productName',
    header: 'Product',
    cell: ({ row }) => (
      <span className="font-medium">{row.original.productName || '—'}</span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.status === 'successful' ? (
        <Badge variant="success">Successful</Badge>
      ) : (
        <Badge variant="destructive">Failed</Badge>
      ),
  },
];

export function BulkUploadResultStep({
  result,
  onStartOver,
}: BulkUploadResultStepProps) {
  const isFullSuccess = result.failed === 0;
  const isPartialSuccess = result.successful > 0 && result.failed > 0;

  return (
    <Card className="border shadow-none">
      <CardHeader className="gap-4 border-b pb-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2 text-primary">
            <CheckCircle2 className="size-5" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-lg">Upload completed</CardTitle>
            <p className="text-sm text-muted-foreground">
              {isFullSuccess
                ? 'Your products have been uploaded successfully.'
                : isPartialSuccess
                  ? `${result.successful} products uploaded successfully. ${result.failed} products require corrections.`
                  : 'Upload failed. Please review the error report and try again.'}
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border bg-muted/20 p-4">
            <p className="text-sm text-muted-foreground">Total rows</p>
            <p className="text-2xl font-semibold">{result.totalRows}</p>
          </div>
          <div className="rounded-lg border bg-green-50 p-4">
            <p className="text-sm text-green-800">Successful</p>
            <p className="text-2xl font-semibold text-green-900">
              {result.successful}
            </p>
          </div>
          <div className="rounded-lg border bg-red-50 p-4">
            <p className="text-sm text-red-800">Failed</p>
            <p className="text-2xl font-semibold text-red-900">
              {result.failed}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-6">
        <div className="overflow-x-auto rounded-xl border bg-white">
          <DataTable columns={columns} data={result.results} />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="outline" onClick={onStartOver}>
            <RotateCcw />
            Upload another file
          </Button>
          <Button asChild>
            <Link to={routes.products}>View uploaded products</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
